// Report enums matching backend
export const ReportStatus = {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  REVISION_NEEDED: 'revision_needed',
} as const;
export type ReportStatus = (typeof ReportStatus)[keyof typeof ReportStatus];

export const ReportType = {
  MARKET: 'market',
  ENVIRONMENTAL: 'environmental',
  ACCIDENT: 'accident',
} as const;
export type ReportType = (typeof ReportType)[keyof typeof ReportType];

export const ReportInterval = {
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
} as const;
export type ReportInterval = (typeof ReportInterval)[keyof typeof ReportInterval];

// Display labels (German)
export const ReportStatusLabels: Record<ReportStatus, string> = {
  [ReportStatus.DRAFT]: 'Entwurf',
  [ReportStatus.IN_PROGRESS]: 'In Bearbeitung',
  [ReportStatus.SUBMITTED]: 'Eingereicht',
  [ReportStatus.APPROVED]: 'Genehmigt',
  [ReportStatus.REVISION_NEEDED]: 'Überarbeitung',
};

export const ReportTypeLabels: Record<ReportType, string> = {
  [ReportType.MARKET]: 'Markt',
  [ReportType.ENVIRONMENTAL]: 'Umwelt',
  [ReportType.ACCIDENT]: 'Unfall',
};

export const ReportIntervalLabels: Record<ReportInterval, string> = {
  [ReportInterval.MONTHLY]: 'Monatlich',
  [ReportInterval.QUARTERLY]: 'Quartal',
  [ReportInterval.YEARLY]: 'Jährlich',
};

// Federal states (Bundesländer) with grouped regions
export const FederalStates = {
  // Nord
  schleswigHolstein: 'Schleswig-Holstein',
  hamburg: 'Hamburg',
  niedersachsen: 'Niedersachsen',
  bremen: 'Bremen',
  // Ost
  berlin: 'Berlin',
  brandenburg: 'Brandenburg',
  mecklenburgVorpommern: 'Mecklenburg-Vorpommern',
  sachsen: 'Sachsen',
  sachsenAnhalt: 'Sachsen-Anhalt',
  thueringen: 'Thüringen',
  // West
  nrw: 'Nordrhein-Westfalen',
  hessen: 'Hessen',
  rheinlandPfalz: 'Rheinland-Pfalz',
  saarland: 'Saarland',
  // Süd
  badenWuerttemberg: 'Baden-Württemberg',
  bayern: 'Bayern',
} as const;

export type FederalStateKey = keyof typeof FederalStates;

export const FederalStatesByRegion = {
  Nord: ['schleswigHolstein', 'hamburg', 'niedersachsen', 'bremen'] as FederalStateKey[],
  Ost: ['berlin', 'brandenburg', 'mecklenburgVorpommern', 'sachsen', 'sachsenAnhalt', 'thueringen'] as FederalStateKey[],
  West: ['nrw', 'hessen', 'rheinlandPfalz', 'saarland'] as FederalStateKey[],
  Süd: ['badenWuerttemberg', 'bayern'] as FederalStateKey[],
};

// Product categories for FormularC (simple label mapping - kept for backwards compatibility)
export const ProductCategories = {
  readyMixConcrete: 'Transportbetonhersteller',
  concreteComponents: 'Beton-Bauteilhersteller',
  prefabConcreteComponents: 'Beton-Fertigteilhersteller',
  concreteGoods: 'Betonwarenhersteller',
  otherSiloCement: 'sonstiger Silozement',
  plasterAndMortar: 'Putz- und Mörtelwerke',
  other: 'Sonstiges',
  baggedCement: 'Sackzement',
} as const;

export type ProductCategoryKey = keyof typeof ProductCategories;

// Hierarchical product category configuration for FormularC form rendering
export interface ProductCategoryConfigItem {
  label: string;
  description: string | null;
  isChild: boolean;
  parentKey?: ProductCategoryKey;
}

export const ProductCategoryConfig: Record<ProductCategoryKey, ProductCategoryConfigItem> = {
  readyMixConcrete: {
    label: 'Transportbetonhersteller',
    description: 'einschließlich Nassmörtel und mobile Anlagen',
    isChild: false,
  },
  concreteComponents: {
    label: 'Beton-Bauteilhersteller',
    description: 'einschließlich Betonwaren, Pflastersteine, Rohre, etc.',
    isChild: false,
  },
  prefabConcreteComponents: {
    label: 'Beton-Fertigteilhersteller',
    description: 'konstruktive Fertigteile, z.B. Wand- und Deckenplatten',
    isChild: true,
    parentKey: 'concreteComponents',
  },
  concreteGoods: {
    label: 'Betonwarenhersteller',
    description: 'z.B. Pflastersteine, Rohre, Mauersteine',
    isChild: true,
    parentKey: 'concreteComponents',
  },
  otherSiloCement: {
    label: 'sonstiger Silozement',
    description: 'z.B. Trockenmörtel, Big Packs',
    isChild: false,
  },
  plasterAndMortar: {
    label: 'Putz- und Mörtelwerke',
    description: null,
    isChild: true,
    parentKey: 'otherSiloCement',
  },
  other: {
    label: 'Sonstiges',
    description: null,
    isChild: true,
    parentKey: 'otherSiloCement',
  },
  baggedCement: {
    label: 'Sackzement',
    description: null,
    isChild: false,
  },
} as const;

// Ordered list of product category keys for proper rendering order
export const ProductCategoryOrder: ProductCategoryKey[] = [
  'readyMixConcrete',
  'concreteComponents',
  'prefabConcreteComponents',
  'concreteGoods',
  'otherSiloCement',
  'plasterAndMortar',
  'other',
  'baggedCement',
];

// Report configuration interface
export interface ReportConfiguration {
  id: number;
  name: string;
  category: ReportType;
  interval: ReportInterval;
  deadlineDays?: number;
}

// Base report interface matching AbstractReport
export interface BaseReport {
  id: number;
  configuration: ReportConfiguration;
  plant: {
    id: number;
    name: string;
    company?: {
      id: number;
      name: string;
    };
  };
  reportingPeriod: string;
  status: ReportStatus;
  createdAt: string;
  updatedAt?: string;
  notes?: string | null;
  /**
   * Nur lesbar, der Server schreibt (Phase 43). Begruendung der letzten Rueckgabe zur
   * Ueberarbeitung; `null`/fehlend, wenn die Meldung nie zurueckgegeben wurde.
   */
  revisionReason?: string | null;
  /**
   * Nur lesbar. `null` = nie zurueckgegeben, `[]` = zurueckgegeben und seitdem nichts
   * geaendert, sonst technische Wurzelfeldnamen der seit der Rueckgabe geaenderten Felder.
   */
  changedFieldsSinceRevision?: string[] | null;
}

// FormularAB interface (16 federal state fields)
export interface FormularAB extends BaseReport {
  badenWuerttemberg?: string | null;
  bayern?: string | null;
  berlin?: string | null;
  brandenburg?: string | null;
  bremen?: string | null;
  hamburg?: string | null;
  hessen?: string | null;
  mecklenburgVorpommern?: string | null;
  niedersachsen?: string | null;
  nrw?: string | null;
  rheinlandPfalz?: string | null;
  saarland?: string | null;
  sachsen?: string | null;
  sachsenAnhalt?: string | null;
  schleswigHolstein?: string | null;
  thueringen?: string | null;
  inlandsVersand?: string | null;
}

// FormularC interface (8 product category fields)
export interface FormularC extends BaseReport {
  readyMixConcrete?: string | null;
  concreteComponents?: string | null;
  prefabConcreteComponents?: string | null;
  concreteGoods?: string | null;
  otherSiloCement?: string | null;
  plasterAndMortar?: string | null;
  other?: string | null;
  baggedCement?: string | null;
  inlandsVersand?: string | null;
}

// FormularD interface (280+ fields)
/**
 * Formular D - Zementartenstatistik
 * Section I: 29 cement types x 9 strength classes
 * Section II: Other binders
 * Section III: Clinker meal
 * Section IV: Contributory product amounts
 */
export interface FormularD extends BaseReport {
  [key: string]: any; // fallback for many fields
  inlandsVersand?: string | null;
  klinkermehl?: string | null;
  beitragspflichtig?: string | null;
}

// ==================== ENVIRONMENTAL DATA TYPES ====================

// Rotary kiln measurement (D-01..D-08/D-18): one kiln can have N measurements,
// each with its own measurementDate/oxygenReference/valueBasis and 26
// discontinuously measured emission values, each with its own BelowLimit flag
// (D-07). No sortOrder, no kilnName -- those live on RotaryKiln. The "Messart"
// selector and methane are gone without replacement (D-04/D-05); the nine
// continuously measured components live on RotaryKiln instead (D-01).
export interface RotaryKilnMeasurement {
  id?: number;
  measurementDate?: string | null;
  oxygenReference?: string | null;
  // "Werte bezogen auf" (D-06): 10 Vol.-% O2 or actual O2 content. No default --
  // a non-null default would make hasAnyData() misreport every freshly created,
  // otherwise empty measurement as "carries data" (see backend doc comment).
  valueBasis?: 'ten_percent' | 'actual' | null;
  // Chlorine and fluorine compounds (mg/m3) -- HCl/HF also exist at the kiln
  // level (D-02); which one applies depends on RotaryKiln.hcl/hfDiscontinuous.
  hclValue?: string | null;
  hclBelowLimit?: boolean;
  hfValue?: string | null;
  hfBelowLimit?: boolean;
  // Heavy metals (mg/m3) -- 16, without mercury (moved to RotaryKiln, D-03)
  cadmiumValue?: string | null;
  cadmiumBelowLimit?: boolean;
  thalliumValue?: string | null;
  thalliumBelowLimit?: boolean;
  antimonyValue?: string | null;
  antimonyBelowLimit?: boolean;
  arsenicValue?: string | null;
  arsenicBelowLimit?: boolean;
  leadValue?: string | null;
  leadBelowLimit?: boolean;
  chromiumValue?: string | null;
  chromiumBelowLimit?: boolean;
  cobaltValue?: string | null;
  cobaltBelowLimit?: boolean;
  copperValue?: string | null;
  copperBelowLimit?: boolean;
  manganeseValue?: string | null;
  manganeseBelowLimit?: boolean;
  nickelValue?: string | null;
  nickelBelowLimit?: boolean;
  vanadiumValue?: string | null;
  vanadiumBelowLimit?: boolean;
  tinValue?: string | null;
  tinBelowLimit?: boolean;
  berylliumValue?: string | null;
  berylliumBelowLimit?: boolean;
  seleniumValue?: string | null;
  seleniumBelowLimit?: boolean;
  telluriumValue?: string | null;
  telluriumBelowLimit?: boolean;
  zincValue?: string | null;
  zincBelowLimit?: boolean;
  // Organic compounds -- 8, without methane and TOC (moved/removed, D-03/D-05)
  pcbDinValue?: string | null;
  pcbDinBelowLimit?: boolean;
  pcbWhoValue?: string | null;
  pcbWhoBelowLimit?: boolean;
  pcddFValue?: string | null;
  pcddFBelowLimit?: boolean;
  pahValue?: string | null;
  pahBelowLimit?: boolean;
  btexValue?: string | null;
  btexBelowLimit?: boolean;
  benzeneValue?: string | null;
  benzeneBelowLimit?: boolean;
  formaldehydeValue?: string | null;
  formaldehydeBelowLimit?: boolean;
  benzoPyreneValue?: string | null;
  benzoPyreneBelowLimit?: boolean;
}

// Phase 28 (D-09/D-15): the master-data kiln embedded on a report's RotaryKiln, exactly the
// fields the backend puts in the 'report:read' group on PlantKiln, plus the JSON-LD identifier
// '@id' -- the save path builds the write IRI from it (see plan 28-06). No `activeFrom`: it is
// not in 'report:read' on the backend (only `activeUntil` carries the D-14 kiln-card note).
export interface RotaryKilnPlantKilnRef {
  id: number;
  name: string;
  activeUntil?: string | null;
  allowsOwnMeasurements: boolean;
  '@id'?: string;
}

// Rotary kiln identity (D-01/D-02): kilnName + sortOrder + its measurements,
// plus the nine annual mean values of the continuously measured emission
// components (dust, NOx, SO2, HCl, HF, CO, NH3, mercury, TOC) that this phase
// moved from the measurement to the kiln. HCl and HF carry an extra
// "measured discontinuously" flag each (hclDiscontinuous/hfDiscontinuous) --
// when set, this kiln has no annual mean for that component and the values
// live exclusively on its individual measurements instead (D-02).
// `measurements` is optional -- a freshly created kiln and a backend payload
// without measurements are both type-valid (D-16: a kiln may exist without
// any measurement at all).
// Phase 28 (D-09/D-15): `plantKiln` carries the master-data identity the kiln was created from.
// `kilnName`/`sortOrder` are still saved (rows are fully replaced on save) but no longer edited
// (D-23) -- the identity for previous-year reordering is `kiln.plantKiln?.id`, not the name.
// Deckung des Massenstrom-Modus (Phase 40, D-22/D-23) -- ausschliesslich report:read, vom Server
// abgeleitet (RotaryKilnLoadCalculationNormalizer bzw. Live-Vorschau-Ergebnis, Plan 40-03/40-05).
// `components` ist bei voller Deckung ein leeres Array (PHP liefert ein leeres assoziatives Array
// als JSON `[]`, nicht `{}`) -- Typ und Zugriffe muessen das tragen, kein `Record<string, ...>`
// allein.
export interface KilnLoadMassFlowCoverage {
  compoundHours: string | null;
  directHours: string | null;
  missingState: 'compound' | 'direct' | null;
  coveredHours: string | null;
  components: Record<string, { missingState: 'compound' | 'direct'; coveredHours: string }> | [];
}

// Das "Rechenblatt" der Frachtenberechnung (Phase 38, D-01/D-02/D-03/D-04): ein optionales
// Hilfsmittel je Drehofen, keine Meldung, eingebettet unter RotaryKiln.loadCalculation (D-21).
// Feldnamen 1:1 aus backend/src/Entity/Reporting/RotaryKilnLoadCalculation.php (RESEARCH.md
// Pitfall 5 -- API-Struktur zuerst, TS-Typen exakt danach ausgerichtet). Sechs Betriebsdaten (D-14: faktisch
// Mindestangabe fuer eine Berechnung, aber keine Pflichtfeldregel) + 33 Konzentrationen (D-11),
// alle `string | null` -- Decimals kommen als String, wie ueberall sonst in diesem Modul.
// weightedFlow/totalOperatingHours/loads liefert ausschliesslich der Server (report:read): TS
// kennt keine Schreibgruppen, die Nur-Lese-Eigenschaft ist reine Backend-/Serializer-Sache.
//
// Phase 40 (D-06/D-09/D-13, VDZ-Weg): 52 Massenstrom-Mittelwerte der 26 diskontinuierlichen
// Komponenten (<key>MassFlowCompound/<key>MassFlowDirect, ausgeschrieben wie die 33
// Konzentrationsfelder oben -- TS-Typen folgen der API 1:1, keine Mapped-Type-Ableitung aus einer
// as-const-Liste, damit ein Property-Zugriff wie `loadCalculation.hclMassFlowCompound` ohne
// Indexsignatur typsicher bleibt), `calculationBasis` (report:read+report:write, D-09) sowie die
// ausschliesslich lesbaren `massFlowCoverage`/`massFlowOrigin` (T-40-19: der Client kann sie
// strukturell schreiben, das Backend ignoriert es).
export interface RotaryKilnLoadCalculation {
  // Betriebsdaten (D-07): Verbundbetrieb (compound*), Direktbetrieb (direct*, optional).
  compoundFlow?: string | null;
  compoundOxygen?: string | null;
  compoundHours?: string | null;
  directFlow?: string | null;
  directOxygen?: string | null;
  directHours?: string | null;
  // 33 Konzentrationen (D-11/D-12, Union aus KILN_ANNUAL_FIELDS/MEASUREMENT_EMISSION_FIELDS-
  // Schluesseln in kilnFields.ts -- KEINE BelowLimit-Felder, D-13).
  dustValue?: string | null;
  noxValue?: string | null;
  so2Value?: string | null;
  coValue?: string | null;
  nh3Value?: string | null;
  mercuryValue?: string | null;
  tocValue?: string | null;
  hclValue?: string | null;
  hfValue?: string | null;
  cadmiumValue?: string | null;
  thalliumValue?: string | null;
  antimonyValue?: string | null;
  arsenicValue?: string | null;
  leadValue?: string | null;
  chromiumValue?: string | null;
  cobaltValue?: string | null;
  copperValue?: string | null;
  manganeseValue?: string | null;
  nickelValue?: string | null;
  vanadiumValue?: string | null;
  tinValue?: string | null;
  berylliumValue?: string | null;
  seleniumValue?: string | null;
  telluriumValue?: string | null;
  zincValue?: string | null;
  pcbDinValue?: string | null;
  pcbWhoValue?: string | null;
  pcddFValue?: string | null;
  pahValue?: string | null;
  btexValue?: string | null;
  benzeneValue?: string | null;
  formaldehydeValue?: string | null;
  benzoPyreneValue?: string | null;
  // 52 Massenstrom-Mittelwerte der 26 diskontinuierlichen Komponenten (Phase 40, D-06), erst alle
  // Compound-, dann alle Direct-Felder je Katalogreihenfolge (chlorFluor, heavyMetals,
  // organicCompounds -- wortgleich mit KILN_LOAD_DISCONTINUOUS_KEYS in kilnFields.ts).
  hclMassFlowCompound?: string | null;
  hclMassFlowDirect?: string | null;
  hfMassFlowCompound?: string | null;
  hfMassFlowDirect?: string | null;
  cadmiumMassFlowCompound?: string | null;
  cadmiumMassFlowDirect?: string | null;
  thalliumMassFlowCompound?: string | null;
  thalliumMassFlowDirect?: string | null;
  antimonyMassFlowCompound?: string | null;
  antimonyMassFlowDirect?: string | null;
  arsenicMassFlowCompound?: string | null;
  arsenicMassFlowDirect?: string | null;
  leadMassFlowCompound?: string | null;
  leadMassFlowDirect?: string | null;
  chromiumMassFlowCompound?: string | null;
  chromiumMassFlowDirect?: string | null;
  cobaltMassFlowCompound?: string | null;
  cobaltMassFlowDirect?: string | null;
  copperMassFlowCompound?: string | null;
  copperMassFlowDirect?: string | null;
  manganeseMassFlowCompound?: string | null;
  manganeseMassFlowDirect?: string | null;
  nickelMassFlowCompound?: string | null;
  nickelMassFlowDirect?: string | null;
  vanadiumMassFlowCompound?: string | null;
  vanadiumMassFlowDirect?: string | null;
  tinMassFlowCompound?: string | null;
  tinMassFlowDirect?: string | null;
  berylliumMassFlowCompound?: string | null;
  berylliumMassFlowDirect?: string | null;
  seleniumMassFlowCompound?: string | null;
  seleniumMassFlowDirect?: string | null;
  telluriumMassFlowCompound?: string | null;
  telluriumMassFlowDirect?: string | null;
  zincMassFlowCompound?: string | null;
  zincMassFlowDirect?: string | null;
  pcbDinMassFlowCompound?: string | null;
  pcbDinMassFlowDirect?: string | null;
  pcbWhoMassFlowCompound?: string | null;
  pcbWhoMassFlowDirect?: string | null;
  pcddFMassFlowCompound?: string | null;
  pcddFMassFlowDirect?: string | null;
  pahMassFlowCompound?: string | null;
  pahMassFlowDirect?: string | null;
  btexMassFlowCompound?: string | null;
  btexMassFlowDirect?: string | null;
  benzeneMassFlowCompound?: string | null;
  benzeneMassFlowDirect?: string | null;
  formaldehydeMassFlowCompound?: string | null;
  formaldehydeMassFlowDirect?: string | null;
  benzoPyreneMassFlowCompound?: string | null;
  benzoPyreneMassFlowDirect?: string | null;
  // Rechengrundlage der diskontinuierlichen Zeilen (Phase 40, D-09): report:read+report:write.
  // `null` bedeutet Konzentration (wie im Rechner) -- hydrate.ts normalisiert das beim Laden auf
  // 'concentration' fuer das SegmentedControl in 40-07.
  calculationBasis?: 'concentration' | 'massFlow' | null;
  // Ausschliesslich report:read (Phase 40, D-22/D-23/D-24, T-40-19): der Client liest sie aus dem
  // gespeicherten Stand, das Backend ignoriert Schreibversuche.
  massFlowCoverage?: KilnLoadMassFlowCoverage | null;
  massFlowOrigin?: { filename: string; acceptedAt: string } | null;
  // Abgeleitet, ausschliesslich report:read (D-03/D-04) -- der Client kann sie strukturell nicht
  // schreiben. weightedFlow/totalOperatingHours: DECIMAL-Anzeigewerte. loads: 33 Eintraege
  // key -> {value, unit}, Einheit mitgespeichert (D-05), weil die D-08-Zuordnung vom Kunden noch
  // nicht bestaetigt ist.
  weightedFlow?: string | null;
  totalOperatingHours?: string | null;
  loads?: Record<string, { value: string; unit: string }> | null;
}

export interface RotaryKiln {
  id?: number;
  kilnName: string;
  sortOrder: number;
  plantKiln?: RotaryKilnPlantKilnRef | null;
  dustValue?: string | null;
  noxValue?: string | null;
  so2Value?: string | null;
  hclValue?: string | null;
  hfValue?: string | null;
  coValue?: string | null;
  nh3Value?: string | null;
  mercuryValue?: string | null;
  tocValue?: string | null;
  hclDiscontinuous?: boolean;
  hfDiscontinuous?: boolean;
  measurements?: RotaryKilnMeasurement[];
  // Phase 38 (D-01/D-15): optional und nullable, exakt wie plantKiln -- dieselbe Vorsicht bei
  // ungeseedeten/alten Datensaetzen (ein Ofen ohne Rechenblatt ist ein gueltiger Zustand).
  loadCalculation?: RotaryKilnLoadCalculation | null;
}

// Primary fuel entry
export interface PrimaryFuelEntry {
  fuelType: string;
  category: string;
  amount?: string | null;
  heatingValue?: string | null;
  emissionFactor?: string | null;
  ashContent?: string | null;
}

// Alternative fuel entry (Phase 34: freie Zeile statt Katalogposition). `rowId` ist die ueber
// Jahre tragende Zeilenidentitaet (D-14) -- daran haengen die abgeleiteten Werte und die
// Vorwerte-Zuordnung (nur amount, siehe previousValuesSource.ts), nicht am Abfallschluessel (der
// bleibt optional) und nicht an der Position in der Liste.
export interface AlternativeFuelEntry {
  rowId: string;
  categoryId: number;
  name: string;
  wasteCode?: string | null;
  amount?: string | null;
  heatingValue?: string | null;
  emissionFactor?: string | null;
  biogenicContent?: string | null;
  ashContent?: string | null;
}

// Die abgeleiteten Zeilenlisten (Phase 30, B-03/B-05/B-09): schreibgeschuetzt,
// ausschliesslich vom EnvironmentalCalculator gefuellt, nie ueber form.setFieldValue
// zurueckgeschrieben -- dieselbe Regel wie bei waterConsumption. Primaerbrennstoffe kennen keine
// biogene CO2-Spalte (kein biogenicContent-Eingabefeld), Sekundaerbrennstoffe schon.
export interface PrimaryFuelDerivedEntry {
  fuelType: string;
  category: string;
  thermalEnergy: string | null;
  fuelAsh: string | null;
}

// Identitaet ist seit Phase 34 die Zeilen-`rowId`, nicht mehr der Katalogschluessel `fuelType`.
export interface AlternativeFuelDerivedEntry {
  rowId: string;
  thermalEnergy: string | null;
  fuelAsh: string | null;
  biogenicCo2: string | null;
}

// Gruppen- und Gesamtsummen der Sekundaerbrennstoffe (Phase 34, D-20/D-21): ausschliesslich
// serverseitig berechnet, nur lesbar -- das Frontend rechnet diese Werte nicht selbst nach
// (Aggregate werden angezeigt, nicht aus Einzelposten wiederhergestellt).
export interface AlternativeFuelSums {
  amount: string | null;
  thermalEnergy: string | null;
  fuelAsh: string | null;
  biogenicCo2: string | null;
}

export interface AlternativeFuelCategorySum extends AlternativeFuelSums {
  categoryId: number;
}

// Input material entry
// Der Primaer-/Sekundaer-Typ der 16 Katalogstoffe steht im Katalog
// (frontend/src/data/inputMaterials.json), nicht hier -- laege er in den Meldungsdaten, koennte
// ein Werk z. B. Kalkstein als Sekundaerrohstoff melden. Deshalb bewusst kein `type`-Feld.
export interface InputMaterialEntry {
  materialType: string;
  clinkerProduction?: string | null;
  cementMilling?: string | null;
}

// Other input material (user-defined)
export interface OtherInputMaterial {
  name: string;
  clinkerProduction?: string | null;
  cementMilling?: string | null;
  type?: 'primary' | 'secondary' | null;
}

// EnvironmentalData full interface
export interface EnvironmentalData extends BaseReport {
  // Production
  clinkerProduction?: string | null;
  clinkerReceiptDomestic?: string | null;
  clinkerReceiptImport?: string | null;
  clinkerShipmentDomestic?: string | null;
  clinkerShipmentExport?: string | null;
  clinkerStockJan01?: string | null;
  clinkerStockDec31?: string | null;
  // P-02 (Phase 32): serverseitig berechnet (31.12. minus 1.1., darf negativ werden),
  // ausschliesslich report:read -- niemals ein Eingabefeld.
  clinkerStockChange?: string | null;
  cementProduction?: string | null;
  dustBypass?: string | null;
  dustExhaustFilter?: string | null;
  electricityConsumptionTotal?: string | null;
  wasteHeatGenerationTotal?: string | null;
  averageClinkerContent?: string | null;
  // Fuels and Materials (JSON)
  primaryFuels?: PrimaryFuelEntry[] | null;
  alternativeFuels?: AlternativeFuelEntry[] | null;
  inputMaterials?: InputMaterialEntry[] | null;
  otherInputMaterials?: OtherInputMaterial[] | null;
  // Rohstoffsummen (Phase 31): serverseitig berechnet, ausschliesslich report:read auf dem
  // Backend -- niemals ein Eingabefeld.
  primaryRawMaterialsClinker?: string | null;
  primaryRawMaterialsCementMilling?: string | null;
  secondaryRawMaterialsClinker?: string | null;
  secondaryRawMaterialsCementMilling?: string | null;
  // Abgeleitete Brennstoffzeilen (Phase 30): read-only, ausschliesslich report:read auf dem
  // Backend -- kommen NICHT in EnvironmentalDataFormData, siehe dort.
  primaryFuelsDerived?: PrimaryFuelDerivedEntry[] | null;
  alternativeFuelsDerived?: AlternativeFuelDerivedEntry[] | null;
  // Gruppen- und Gesamtsummen der Sekundaerbrennstoffe (Phase 34, D-20/D-21): dieselbe
  // Platzierung wie die Zeile darueber -- read-only, ausschliesslich report:read auf dem
  // Backend, kommen NICHT in EnvironmentalDataFormData. Ersetzen das entfallene abgeleitete
  // Sonstige-Feld an dieser Stelle.
  alternativeFuelCategorySums?: AlternativeFuelCategorySum[] | null;
  alternativeFuelTotalSums?: AlternativeFuelSums | null;
  // Emission Reduction - NOx
  noxStagedCombustion?: boolean | null;
  noxSncr?: boolean | null;
  noxScr?: boolean | null;
  noxOther?: boolean | null;
  noxOtherDescription?: string | null;
  // Emission Reduction - SO2
  so2Additives?: boolean | null;
  so2WetScrubber?: boolean | null;
  so2DryScrubber?: boolean | null;
  so2Other?: boolean | null;
  so2OtherDescription?: string | null;
  // Emission Reduction - Dust
  dustReductionBagFilter?: boolean | null;
  dustReductionElectrostaticFilter?: boolean | null;
  dustReductionHybridFilter?: boolean | null;
  // EM-02/EM-03 (Phase 32): analog zu noxOther/so2Other/hgOther -- Freitext bleibt dauerhaft
  // optional (Kundenentscheidung EM-03, Konsistenz zu den drei bestehenden *OtherDescription).
  dustReductionOther?: boolean | null;
  dustReductionOtherDescription?: string | null;
  // Emission Reduction - Hg (Quecksilber)
  hgFilterDustDischarge?: boolean | null;
  hgActivatedCarbon?: boolean | null;
  hgHearthFurnaceCoke?: boolean | null;
  hgOther?: boolean | null;
  hgOtherDescription?: string | null;
  // Water
  waterWithdrawal?: string | null;
  waterDischarge?: string | null;
  waterConsumption?: string | null;
  waterRecycled?: string | null;
  // Quarries / Biodiversity
  quarryAreaTotal?: string | null;
  // SB-02 (Phase 32): Eingabefeld, beim Anlegen einer Folgemeldung vom Initializer aus der
  // Vorjahresmeldung vorbelegt; im ersten Meldejahr leer und eingebbar.
  rehabilitatedAreaPriorYears?: string | null;
  rehabilitatedAreaCurrentYear?: string | null;
  // SB-02 (Phase 32): seit dieser Phase serverseitig berechnet
  // (rehabilitatedAreaPriorYears + rehabilitatedAreaCurrentYear), ausschliesslich report:read
  // im Backend -- im Formular nur noch Anzeige, kein Eingabefeld mehr.
  rehabilitatedAreaTotal?: string | null;
  rehabilitationNativeForest?: string | null;
  rehabilitationGrassland?: string | null;
  rehabilitationWetlands?: string | null;
  rehabilitationWaterBodies?: string | null;
  rehabilitationStreams?: string | null;
  rehabilitationLakesBiodiversity?: string | null;
  rehabilitationLakesStorage?: string | null;
  rehabilitationLakesRecreation?: string | null;
  rehabilitationExtensiveAgriculture?: string | null;
  rehabilitationConventionalAgriculture?: string | null;
  rehabilitationCommercialForest?: string | null;
  rehabilitationParkRecreation?: string | null;
  rehabilitationCommercialIndustrial?: string | null;
  rehabilitationOther?: string | null;
  activeQuarryArea?: string | null;
  temporaryHabitat?: string | null;
  // Rotary Kilns
  rotaryKilns?: RotaryKiln[];
}

// ==================== ACCIDENT DATA TYPES ====================

// Employee categories for accident matrix columns
export const AccidentEmployeeCategories = {
  wage: 'Lohnbelegschaft',
  admin: 'Angestellte in der Verwaltung',
  temp: 'Kräfte aus Arbeitnehmerüberlassung',
  total: 'Gesamt',
} as const;

export type AccidentEmployeeCategoryKey = keyof typeof AccidentEmployeeCategories;

// Matrix row definitions
export const AccidentMatrixRows = {
  hoursWorked: 'Verfahrene Arbeitsstunden',
  accidentsLostDay: 'Mit mindestens 1 Ausfalltag',
  lostWorkDays: 'Arbeitsausfalltage zu Reihe 2',
} as const;

export type AccidentMatrixRowKey = keyof typeof AccidentMatrixRows;

// AccidentData full interface
export interface AccidentData extends BaseReport {
  // General Information
  clinkerProductionDuringPeriod?: boolean | null;
  // Employee Counts
  employeesTotal?: number | null;
  employeesAdmin?: number | null;
  // Contact Person
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  // Matrix Row 1 — Verfahrene Arbeitsstunden (decimal strings)
  hoursWorkedWage?: string | null;
  hoursWorkedAdmin?: string | null;
  hoursWorkedTemp?: string | null;
  hoursWorkedTotal?: string | null;
  // Matrix Row 2 — Mit mindestens 1 Ausfalltag
  accidentsLostDayWage?: number | null;
  accidentsLostDayAdmin?: number | null;
  accidentsLostDayTemp?: number | null;
  accidentsLostDayTotal?: number | null;
  // Matrix Row 3 — Arbeitsausfalltage zu Reihe 2
  lostWorkDaysWage?: number | null;
  lostWorkDaysAdmin?: number | null;
  lostWorkDaysTemp?: number | null;
  lostWorkDaysTotal?: number | null;
  // MTI own employees
  mtiOwnEmployees?: number | null;
  // Indirect Employees
  indirectHoursWorked?: string | null;
  indirectLtis?: number | null;
  // Unfalltote (manual)
  fatalitiesWage?: number | null;
  fatalitiesAdmin?: number | null;
  fatalitiesTemp?: number | null;
  fatalitiesCommuting?: number | null;
  indirectFatalities?: number | null;
  mtiThirdParties?: number | null;
  // Comments
  comments?: string | null;
  // Computed (read-only, server-authoritative; also returned by the calculate endpoint)
  annualHoursWage?: string | null;
  annualHoursAdmin?: string | null;
  annualHoursTemp?: string | null;
  annualHoursTotal?: string | null;
  annualHoursCombined?: string | null;
  accidentRateOwn?: string | null;
  accidentRateIndirect?: string | null;
  accidentRateTotal?: string | null;
}

// Union type for all report types
export type Report = FormularAB | FormularC | FormularD | EnvironmentalData | AccidentData;

// Form data types for updates
export interface FormularABFormData {
  badenWuerttemberg?: string | null;
  bayern?: string | null;
  berlin?: string | null;
  brandenburg?: string | null;
  bremen?: string | null;
  hamburg?: string | null;
  hessen?: string | null;
  mecklenburgVorpommern?: string | null;
  niedersachsen?: string | null;
  nrw?: string | null;
  rheinlandPfalz?: string | null;
  saarland?: string | null;
  sachsen?: string | null;
  sachsenAnhalt?: string | null;
  schleswigHolstein?: string | null;
  thueringen?: string | null;
  inlandsVersand?: string | null;
  status?: ReportStatus;
  notes?: string | null;
}

export interface FormularCFormData {
  [key: string]: string | null | ReportStatus | undefined;
  readyMixConcrete?: string | null;
  concreteComponents?: string | null;
  prefabConcreteComponents?: string | null;
  concreteGoods?: string | null;
  otherSiloCement?: string | null;
  plasterAndMortar?: string | null;
  other?: string | null;
  baggedCement?: string | null;
  inlandsVersand?: string | null;
  status?: ReportStatus;
  notes?: string | null;
}

export interface FormularDFormData {
  [key: string]: string | null | ReportStatus | undefined;
  inlandsVersand?: string | null;
  status?: ReportStatus;
  notes?: string | null;
}

export interface EnvironmentalDataFormData {
  [key: string]: unknown;
  status?: ReportStatus;
  notes?: string | null;
  // Production
  clinkerProduction?: string | null;
  clinkerReceiptDomestic?: string | null;
  clinkerReceiptImport?: string | null;
  clinkerShipmentDomestic?: string | null;
  clinkerShipmentExport?: string | null;
  clinkerStockJan01?: string | null;
  clinkerStockDec31?: string | null;
  // P-02 (Phase 32): kein Eingabefeld -- reist wie waterConsumption und die vier
  // Rohstoffsummen nur mit, damit der Vorwerte-Pfad (flattenReportValues ueber das
  // Hydrierungsergebnis) den mitgefuehrten Anzeigewert findet.
  clinkerStockChange?: string | null;
  cementProduction?: string | null;
  dustBypass?: string | null;
  dustExhaustFilter?: string | null;
  electricityConsumptionTotal?: string | null;
  wasteHeatGenerationTotal?: string | null;
  averageClinkerContent?: string | null;
  // Fuels and Materials (JSON)
  primaryFuels?: PrimaryFuelEntry[] | null;
  alternativeFuels?: AlternativeFuelEntry[] | null;
  inputMaterials?: InputMaterialEntry[] | null;
  otherInputMaterials?: OtherInputMaterial[] | null;
  // Rohstoffsummen (Phase 31): kein Eingabefeld -- reisen wie waterConsumption nur mit, damit der
  // Vorwerte-Pfad (flattenReportValues ueber das Hydrierungsergebnis) sie findet. Mitgefuehrter
  // Anzeigewert, niemals ein Feld, das der Client schreibt.
  primaryRawMaterialsClinker?: string | null;
  primaryRawMaterialsCementMilling?: string | null;
  secondaryRawMaterialsClinker?: string | null;
  secondaryRawMaterialsCementMilling?: string | null;
  // Emission Reduction - NOx
  noxStagedCombustion?: boolean | null;
  noxSncr?: boolean | null;
  noxScr?: boolean | null;
  noxOther?: boolean | null;
  noxOtherDescription?: string | null;
  // Emission Reduction - SO2
  so2Additives?: boolean | null;
  so2WetScrubber?: boolean | null;
  so2DryScrubber?: boolean | null;
  so2Other?: boolean | null;
  so2OtherDescription?: string | null;
  // Emission Reduction - Dust
  dustReductionBagFilter?: boolean | null;
  dustReductionElectrostaticFilter?: boolean | null;
  dustReductionHybridFilter?: boolean | null;
  // EM-02/EM-03 (Phase 32): analog zu noxOther/so2Other/hgOther -- Freitext bleibt dauerhaft
  // optional (Kundenentscheidung EM-03, Konsistenz zu den drei bestehenden *OtherDescription).
  dustReductionOther?: boolean | null;
  dustReductionOtherDescription?: string | null;
  // Emission Reduction - Hg (Quecksilber)
  hgFilterDustDischarge?: boolean | null;
  hgActivatedCarbon?: boolean | null;
  hgHearthFurnaceCoke?: boolean | null;
  hgOther?: boolean | null;
  hgOtherDescription?: string | null;
  // Water
  waterWithdrawal?: string | null;
  waterDischarge?: string | null;
  waterConsumption?: string | null;
  waterRecycled?: string | null;
  // Quarries / Biodiversity
  quarryAreaTotal?: string | null;
  // SB-02 (Phase 32): Eingabefeld, beim Anlegen einer Folgemeldung vom Initializer aus der
  // Vorjahresmeldung vorbelegt; im ersten Meldejahr leer und eingebbar.
  rehabilitatedAreaPriorYears?: string | null;
  rehabilitatedAreaCurrentYear?: string | null;
  // SB-02 (Phase 32): seit dieser Phase serverseitig berechnet
  // (rehabilitatedAreaPriorYears + rehabilitatedAreaCurrentYear), ausschliesslich report:read
  // im Backend -- im Formular nur noch Anzeige, kein Eingabefeld mehr.
  rehabilitatedAreaTotal?: string | null;
  rehabilitationNativeForest?: string | null;
  rehabilitationGrassland?: string | null;
  rehabilitationWetlands?: string | null;
  rehabilitationWaterBodies?: string | null;
  rehabilitationStreams?: string | null;
  rehabilitationLakesBiodiversity?: string | null;
  rehabilitationLakesStorage?: string | null;
  rehabilitationLakesRecreation?: string | null;
  rehabilitationExtensiveAgriculture?: string | null;
  rehabilitationConventionalAgriculture?: string | null;
  rehabilitationCommercialForest?: string | null;
  rehabilitationParkRecreation?: string | null;
  rehabilitationCommercialIndustrial?: string | null;
  rehabilitationOther?: string | null;
  activeQuarryArea?: string | null;
  temporaryHabitat?: string | null;
  // Rotary Kilns
  rotaryKilns?: RotaryKiln[];
}

export interface AccidentDataFormData {
  [key: string]: unknown;
  status?: ReportStatus;
  notes?: string | null;
  // General Information
  clinkerProductionDuringPeriod?: boolean | null;
  // Employee Counts
  employeesTotal?: number | null;
  employeesAdmin?: number | null;
  // Contact Person
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  // Matrix Row 1
  hoursWorkedWage?: string | null;
  hoursWorkedAdmin?: string | null;
  hoursWorkedTemp?: string | null;
  hoursWorkedTotal?: string | null;
  // Matrix Row 2
  accidentsLostDayWage?: number | null;
  accidentsLostDayAdmin?: number | null;
  accidentsLostDayTemp?: number | null;
  accidentsLostDayTotal?: number | null;
  // Matrix Row 3
  lostWorkDaysWage?: number | null;
  lostWorkDaysAdmin?: number | null;
  lostWorkDaysTemp?: number | null;
  lostWorkDaysTotal?: number | null;
  // MTI own employees
  mtiOwnEmployees?: number | null;
  // Indirect Employees
  indirectHoursWorked?: string | null;
  indirectLtis?: number | null;
  // Unfalltote (manual)
  fatalitiesWage?: number | null;
  fatalitiesAdmin?: number | null;
  fatalitiesTemp?: number | null;
  fatalitiesCommuting?: number | null;
  indirectFatalities?: number | null;
  mtiThirdParties?: number | null;
  // Comments
  comments?: string | null;
  // C-01 (Frontend): einziges nicht editierbares Feld im Form-State. Reist nur mit, damit der
  // eingefrorene KANUE-Wert der Meldung alle Payload-Wege (Vorschau /api/reports/calculate UND
  // Validierung /api/reports/validate) ohne Sondermechanik erreicht -- siehe hydrateAccidentData
  // und ACCIDENT_CALC_INPUT_KEYS.
  annualHoursTemp?: string | null;
}

/** Shape returned by POST /api/reports/calculate for type "environmental" (and the computed subset of EnvironmentalData). */
export interface EnvironmentalCalculations {
  waterConsumption: string | null;
  // Pflichtig, aber nullable (analog waterConsumption) -- zwingt jeden Aufbauort (Live-Vorschau-
  // Antwort, initialCalc-Seed in ReportEditPage.tsx bzw. buildEnvironmentalInitialCalc in Plan 05),
  // alle vier Schluessel explizit zu setzen statt einen stillschweigend wegzulassen.
  primaryFuelsDerived: PrimaryFuelDerivedEntry[] | null;
  alternativeFuelsDerived: AlternativeFuelDerivedEntry[] | null;
  // Gruppen- und Gesamtsummen der Sekundaerbrennstoffe (Phase 34, D-20/D-21): dieselbe
  // Pflicht-aber-nullable-Begruendung wie oben -- ersetzen das entfallene abgeleitete
  // Sonstige-Feld. Jeder Aufbauort muss beide Schluessel explizit setzen.
  alternativeFuelCategorySums: AlternativeFuelCategorySum[] | null;
  alternativeFuelTotalSums: AlternativeFuelSums | null;
  // Rohstoffsummen (Phase 31): dieselbe Pflicht-aber-nullable-Begruendung wie oben --
  // folgen dem waterConsumption-Skalarmuster, nicht dem *Derived-Array-Muster.
  primaryRawMaterialsClinker: string | null;
  primaryRawMaterialsCementMilling: string | null;
  secondaryRawMaterialsClinker: string | null;
  secondaryRawMaterialsCementMilling: string | null;
  // P-02/SB-02 (Phase 32): dieselbe Pflicht-aber-nullable-Begruendung wie oben -- folgen
  // ebenfalls dem waterConsumption-Skalarmuster, nicht dem *Derived-Array-Muster.
  clinkerStockChange: string | null;
  rehabilitatedAreaTotal: string | null;
  // Frachtenberechnung (Phase 38, D-19): eine Liste in Ofenreihenfolge, je Eintrag das Ergebnis
  // von RotaryKilnLoadCalculation. Pflichtig, aber nullable wie die uebrigen Schluessel --
  // erzwingt, dass jeder Aufbauort (Live-Vorschau-Antwort, buildEnvironmentalInitialCalc) den
  // Schluessel explizit setzt statt ihn stillschweigend wegzulassen.
  rotaryKilnLoads: RotaryKilnLoadResult[] | null;
}

// Ergebnis eines einzelnen Ofens aus dem Vorschau-/Speicherzweig der Frachtenberechnung (Phase
// 38, D-19; erweitert Phase 40, D-22/D-23) -- dieselben abgeleiteten Felder wie an
// RotaryKilnLoadCalculation, aber ohne die 91 Eingaben: die Live-Vorschau liefert nur das
// Rechenergebnis, nicht die Eingaben zurueck. massFlowCoverage kommt hier zusaetzlich aus der
// Live-Vorschau-Antwort (POST /api/reports/calculate); initialCalc.ts uebernimmt es beim
// Oeffnen aus dem gespeicherten Stand (kiln.loadCalculation.massFlowCoverage).
export interface RotaryKilnLoadResult {
  weightedFlow: string | null;
  totalOperatingHours: string | null;
  loads: Record<string, { value: string; unit: string }> | null;
  massFlowCoverage: KilnLoadMassFlowCoverage | null;
}

/** Shape returned by POST /api/reports/calculate for type "accident" (and the computed subset of AccidentData). */
export interface AccidentCalculations {
  hoursWorkedTotal: string | null;
  accidentsLostDayTotal: number | null;
  lostWorkDaysTotal: number | null;
  annualHoursWage: string | null;
  annualHoursAdmin: string | null;
  // Eingefrorener Wert der Meldung bzw. aktueller Einstellungswert (C-01) -- nie mehr eine feste Konstante.
  annualHoursTemp: string | null;
  annualHoursTotal: string | null;
  // Durchschnittliche Jahresarbeitsstunden ohne KANUE (Plan 25.1-04): Pflichtschluessel wie alle
  // anderen -- erzwingt, dass jedes Objektliteral dieses Typs den neuen Wert setzt.
  annualHoursCombined: string | null;
  accidentRateOwn: string | null;
  accidentRateIndirect: string | null;
  accidentRateTotal: string | null;
}

// Report type identifiers for routing
export const ReportTypeIdentifier = {
  FORMULAR_AB: 'formular-ab',
  FORMULAR_C: 'formular-c',
  FORMULAR_D: 'formular-d',
  ENVIRONMENTAL: 'environmental',
  ACCIDENT: 'accident',
} as const;
export type ReportTypeIdentifier = (typeof ReportTypeIdentifier)[keyof typeof ReportTypeIdentifier];

export const ReportTypeIdentifierLabels: Record<ReportTypeIdentifier, string> = {
  [ReportTypeIdentifier.FORMULAR_AB]: 'Inlandsversand',
  [ReportTypeIdentifier.FORMULAR_C]: 'Versand nach Produktkategorien',
  [ReportTypeIdentifier.FORMULAR_D]: 'Zementartenstatistik',
  [ReportTypeIdentifier.ENVIRONMENTAL]: 'Umweltdaten',
  [ReportTypeIdentifier.ACCIDENT]: 'Unfalldaten',
};

/**
 * Ordnet jeden Meldungstyp seiner Kategorie zu (Phase 25, `reportAccess.ts`). Diese Zuordnung
 * darf im Frontend liegen, weil `category()` auf jeder der fuenf Report-Entities eine abstrakte
 * statische Methode ist -- strukturell hartverdrahtet, nicht konfiguriert
 * (siehe backend/src/Entity/Reporting/{Market/FormularAB,Market/FormularC,Market/FormularD,
 * EnvironmentalData,AccidentData}.php::category()). Fuer `interval` gilt das AUSDRUECKLICH NICHT:
 * das ist eine DB-Spalte und wurde in 22-02 per Migration geaendert, nicht hartverdrahtet.
 */
export const ReportTypeCategory: Record<ReportTypeIdentifier, ReportType> = {
  [ReportTypeIdentifier.FORMULAR_AB]: ReportType.MARKET,
  [ReportTypeIdentifier.FORMULAR_C]: ReportType.MARKET,
  [ReportTypeIdentifier.FORMULAR_D]: ReportType.MARKET,
  [ReportTypeIdentifier.ENVIRONMENTAL]: ReportType.ENVIRONMENTAL,
  [ReportTypeIdentifier.ACCIDENT]: ReportType.ACCIDENT,
};

// Metadata for FormularD
export const FormularDCementTypes = {
  cemI: 'Portlandzement CEM I',
  cemII_A_S: 'Portlandhüttenzement CEM II/A-S',
  cemII_B_S: 'Portlandhüttenzement CEM II/B-S',
  cemII_A_D: 'Portlandsilicastaubzement CEM II/A-D',
  cemII_A_P: 'Portlandpuzzolanzement CEM II/A-P',
  cemII_B_P: 'Portlandpuzzolanzement CEM II/B-P',
  cemII_A_Q: 'Portlandpuzzolanzement CEM II/A-Q',
  cemII_B_Q: 'Portlandpuzzolanzement CEM II/B-Q',
  cemII_A_V: 'Portlandflugaschezement CEM II/A-V',
  cemII_B_V: 'Portlandflugaschezement CEM II/B-V',
  cemII_A_W: 'Portlandflugaschezement CEM II/A-W',
  cemII_B_W: 'Portlandflugaschezement CEM II/B-W',
  cemII_A_T: 'Portlandölschieferzement CEM II/A-T',
  cemII_B_T: 'Portlandölschieferzement CEM II/B-T',
  cemII_A_L: 'Portlandkalksteinzement CEM II/A-L',
  cemII_B_L: 'Portlandkalksteinzement CEM II/B-L',
  cemII_A_LL: 'Portlandkalksteinzement CEM II/A-LL',
  cemII_B_LL: 'Portlandkalksteinzement CEM II/B-LL',
  cemII_A_M: 'Portlandkompositzement CEM II/A-M',
  cemII_B_M: 'Portlandkompositzement CEM II/B-M',
  cemII_C_M: 'Portlandkompositzement CEM II/C-M',
  cemIII_A: 'Hochofenzement CEM III/A',
  cemIII_B: 'Hochofenzement CEM III/B',
  cemIII_C: 'Hochofenzement CEM III/C',
  cemIV_A: 'Puzzolanzement CEM IV/A',
  cemIV_B: 'Puzzolanzement CEM IV/B',
  cemV_A: 'Kompositzement CEM V/A',
  cemV_B: 'Kompositzement CEM V/B',
  cemVI: 'Kompositzement CEM VI',
} as const;

export type FormularDCementTypeKey = keyof typeof FormularDCementTypes;

export const FormularDStrengths = {
  '32_5_L': '32,5 L',
  '32_5_N': '32,5 N',
  '32_5_R': '32,5 R',
  '42_5_L': '42,5 L',
  '42_5_N': '42,5 N',
  '42_5_R': '42,5 R',
  '52_5_L': '52,5 L',
  '52_5_N': '52,5 N',
  '52_5_R': '52,5 R',
} as const;

export type FormularDStrengthKey = keyof typeof FormularDStrengths;

export const FormularDSection2a = {
  zugelassene_32_5_N: '32,5 N',
  zugelassene_32_5_R: '32,5 R',
  zugelassene_42_5_N: '42,5 N',
  zugelassene_42_5_R: '42,5 R',
  zugelassene_52_5_N: '52,5 N',
  zugelassene_52_5_R: '52,5 R',
} as const;

export const FormularDSection2b = {
  putz_5_0: '5,0',
  putz_12_5: '12,5',
  putz_12_5_X: '12,5 X',
  putz_22_5: '22,5',
  putz_22_5_X: '22,5 X',
} as const;

export const FormularDSection2c = {
  tragschicht_E2: 'E 2',
  tragschicht_E3: 'E 3',
  tragschicht_E4: 'E 4',
  tragschicht_E4_RS: 'E4-RS',
} as const;

// Filter interface for report list queries
export interface ReportFilters {
  plant?: number;
  // ReportStatus | ReportStatus[] — eine Liste erzeugt in buildQueryParams mehrere
  // `status[]=...`-Parameter (SQL-IN gegen den bestehenden SearchFilter, D-02 aus
  // 24-CONTEXT.md). Ein Einzelwert bleibt unveraendert `status=...` (z. B. getDraftCounts).
  status?: ReportStatus | ReportStatus[];
  reportType?: ReportTypeIdentifier;
  // Gewaehltes Berichtsjahr (D-05). `undefined` heisst alle Jahre -- ausdruecklich optional,
  // damit `getDraftCounts()` -> `getAllReports({ status: 'draft' })` ohne Jahr weiterhin keinen
  // Zeitparameter erzeugt (Pitfall 5, 25-RESEARCH.md).
  year?: number;
  // Gewaehlte Periode innerhalb des Jahres als 1-basierter Index ('1'..'12' bzw. '1'..'4');
  // `undefined` heisst alle Perioden (D-07). Reine Anzeigelogik wie `year`, wird von
  // `buildQueryParams` nicht ausgewertet -- die Umrechnung macht der Aufrufer ueber
  // `reportingPeriodRange` (frontend/src/utils/reportYearRange.ts).
  period?: string;
  // Bereichsgrenzen im Format YYYY-MM-DD, abgeleitet aus `year`/`period` ueber
  // `reportingPeriodRange`. Erzeugen in `buildQueryParams` `reportingPeriod[after]`/`[before]`.
  reportingPeriodAfter?: string;
  reportingPeriodBefore?: string;
  // Exact-Match auf `reportingPeriod` -- bewusst NICHT veraltet, hat seit Plan 25-04 genau EINEN
  // aktiven Aufrufer: `usePreviousValuesLoader.ts:208` laedt darueber die Vorjahreswerte
  // (`api.getAll({ plant, reportingPeriod: previousPeriod, status })`,
  // `resolvePreviousReport()` nimmt anschliessend `list[0]`). Die Meldungenseite
  // (`ReportsPage.tsx`) setzt seit diesem Plan `reportingPeriodAfter`/`Before` statt dieses
  // Feldes und gehoert damit nicht mehr zu seinen Aufrufern. Nicht entfernen, sonst liefert
  // `resolvePreviousReport()` still eine beliebige Meldung des Werks als "Vorjahr" zurueck.
  reportingPeriod?: string;
}

